import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import * as Sentry from '@sentry/browser';
import { RewriteFrames } from '@sentry/integrations';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import { TransactionContext } from '@sentry/types';
import { config } from 'rxjs';

import { AccountBase, SentryConfig } from './config';
import { RtLocation } from './location.service';

@Injectable()
export class RtcErrorHandler implements ErrorHandler {
  private readonly filteredStatuses: number[] = [0, 401, 403, 404];
  private readonly appRevision: string = this.injector.get(this.sentryConfig.revisionToken);
  private readonly ignoredEvents: (string | RegExp)[] = [
    'ChunkLoadError',
    'HTTP request failed',
    'Socket close code 1000',
    '[Analytics] Segment client failed to load',
  ];
  private nextPath: string;
  private dialogActive = false;

  // eslint-disable-next-line max-lines-per-function
  constructor(
    private sentryConfig: SentryConfig,
    private injector: Injector,
    private route: ActivatedRoute,
    private router: Router,
    private location: RtLocation,
  ) {
    if (!this.sentryConfig.enable) {
      return;
    }

    this.location.go$.subscribe(path => (this.nextPath = path));

    config.onUnhandledError = (err: any) => this.rxjsOnUnhandledError(err);

    Sentry.init({
      dsn: this.sentryConfig.dsn,
      release: this.appRevision,
      integrations: [
        new RewriteFrames({
          iteratee: frame => {
            if (frame.filename) {
              frame.in_app = /(.*)(runtime|main|common|\d+)\.(.*)\.js/.test(frame.filename);
            }
            return frame;
          },
        }),
        new TracingIntegrations.BrowserTracing({
          beforeNavigate: (context: TransactionContext): TransactionContext => ({
            ...context,
            ...this.getTransactionName(context),
          }),
        }),
      ],
      tracesSampleRate: 0.5,
      beforeBreadcrumb: (
        breadcrumb: Sentry.Breadcrumb,
        hint?: Record<string, string>,
      ): Sentry.Breadcrumb | null =>
        // This will filter out clicks/typing
        breadcrumb.category && breadcrumb.category.includes('ui.') ? null : breadcrumb,
      beforeSend: (
        event: Sentry.Event,
        hint?: Sentry.EventHint,
      ): PromiseLike<Sentry.Event | null> | Sentry.Event | null => {
        if (!this.sentryConfig.enable) return null;
        // @ts-ignore
        const httpStatus = event.extra?.__serialized__?.status;
        const isStatusValid =
          httpStatus === undefined || !this.filteredStatuses.includes(httpStatus);

        return isStatusValid ? event : null;
      },
      maxBreadcrumbs: 30,
      ignoreErrors: this.ignoredEvents,
    });

    /**
     * NgRx store devtools requires ErrorService
     * Here we need currentAdmin but it is empty because AccountBase is not initialized yet
     * setTimeout fixes this issue
     */
    setTimeout(() => {
      const account = this.injector.get(AccountBase);
      account.currentAdmin$.subscribe(currentAdmin =>
        Sentry.setUser({
          id: `${currentAdmin.id}`,
          email: currentAdmin.email,
          username: `${currentAdmin.firstName} ${currentAdmin.lastName}`,
          roleName: currentAdmin.roleName,
        }),
      );

      account.currentCompany$.subscribe(currentCompany =>
        Sentry.setExtra('Company', currentCompany?.id),
      );
    });
  }

  public handleError(err: Error, showInConsole: boolean = !this.sentryConfig.enable): void {
    try {
      if (this.sentryConfig.enable) {
        if (err instanceof HttpErrorResponse) {
          if (!this.filteredStatuses.includes(err.status)) {
            Sentry.captureMessage(this.getHttpErrorResponseMessage(err));
          }
        } else {
          this.sendErrorToSentry(err);
        }
      }
    } finally {
      if (showInConsole) {
        console.error(err);
      }
    }
  }

  public trace(name: string, showInConsole: boolean): void {
    try {
      throw new Error(name);
    } catch (e) {
      this.handleError(e, showInConsole);
    }
  }

  private rxjsOnUnhandledError(err: any) {
    if (err instanceof Error) return this.handleError(err);

    try {
      const message = this.getErrorTypeMessage(err);
      throw new Error(`Unhandled rxjs error ${message}`);
    } catch (e) {
      this.handleError(e);
    }
  }

  private getErrorTypeMessage(err: any) {
    if (err instanceof HttpErrorResponse) {
      return this.getHttpErrorResponseMessage(err);
    } else if (typeof err === 'object' && err !== null) {
      return `keys: ${Object.keys(err)}`;
    } else {
      return `value: ${err}`;
    }
  }

  private getHttpErrorResponseMessage(response: HttpErrorResponse): string {
    return `Http request failed. (${response.url}, ${response.status}, ${response.message})`;
  }

  private sendErrorToSentry(err: any): void {
    Sentry.setTags({ fatal: 'true' });
    Sentry.captureException(err.originalError || err);
  }

  private getTransactionName(context: TransactionContext): TransactionContext {
    if (context.op === 'pageload') {
      return { name: context.op };
    }

    const dialogOutletName = 'dialog';
    const { queryParams: nextQueryParams } = this.router.parseUrl(this.nextPath);
    if (nextQueryParams.candidate) {
      this.dialogActive = true;
      return {
        name: [
          '/candidate_dialog',
          nextQueryParams.tab || 'overview',
          nextQueryParams.openInterview === 'true' && 'open_interview',
        ]
          .filter(Boolean)
          .join('/'),
      };
    }

    const primaryOutlet = this.route.children.find(({ outlet }) => 'primary' === outlet) || null;
    const dialogOutlet = this.route.children.find(({ outlet }) => 'dialog' === outlet) || null;

    const primaryPath = '/' + this.getPathFromRoute(primaryOutlet);
    const dialogPath = this.getPathFromRoute(dialogOutlet);

    if (dialogPath) {
      this.dialogActive = true;
      return { name: `${dialogOutletName}:${dialogPath}` };
    }

    if (this.dialogActive) {
      this.dialogActive = false;
      return { name: primaryPath, sampled: false };
    }

    return { name: primaryPath };
  }

  private getPathFromRoute(activatedRoute: ActivatedRoute | null) {
    let route: ActivatedRoute | null = activatedRoute;
    let path: string[] = [];
    while (route) {
      const { routeConfig } = route;
      if (routeConfig && routeConfig.path) {
        path = [...path, ...routeConfig.path.split('/')];
      }
      route = route.firstChild;
    }
    return path.join('/');
  }
}
